import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useTRPC } from "@/integrations/trpc/react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/buyers/reset-password")({
	component: ResetPasswordPage,
});

function ResetPasswordPage() {
	const trpc = useTRPC();
	const navigate = useNavigate();
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [error, setError] = useState<string | null>(null);

	// Check if we have a valid session (user came from email link)
	useEffect(() => {
		// Supabase automatically exchanges the token for a session
		// when the user clicks the email link
		const checkSession = async () => {
			const { supabase } = await import("@/lib/supabase-browser");
			const { data } = await supabase.auth.getSession();
			if (!data.session) {
				toast.error("Invalid or expired reset link");
				navigate({ to: "/buyers/forgot-password" });
			}
		};
		checkSession();
	}, [navigate]);

	const resetPasswordMutation = useMutation(
		trpc.auth.resetPassword.mutationOptions({
			onSuccess: () => {
				toast.success("Password reset successfully!");
				navigate({ to: "/buyers/login" });
			},
			onError: (error) => {
				toast.error(`Failed to reset password: ${error.message}`);
			},
		}),
	);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (password.length < 8) {
			setError("Password must be at least 8 characters");
			return;
		}

		if (password !== confirmPassword) {
			setError("Passwords do not match");
			return;
		}

		resetPasswordMutation.mutate({ newPassword: password });
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
			<Card className="w-full max-w-md">
				<CardHeader className="space-y-1">
					<CardTitle className="text-2xl font-bold text-center">
						Reset your password
					</CardTitle>
					<CardDescription className="text-center">
						Enter your new password below
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="password">New Password</Label>
							<Input
								id="password"
								type="password"
								placeholder="Enter new password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="confirmPassword">Confirm Password</Label>
							<Input
								id="confirmPassword"
								type="password"
								placeholder="Confirm new password"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								required
							/>
						</div>

						{error && <p className="text-sm text-red-500">{error}</p>}

						<Button
							type="submit"
							className="w-full"
							disabled={resetPasswordMutation.isPending}
						>
							{resetPasswordMutation.isPending
								? "Resetting..."
								: "Reset Password"}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
